import { useMemo } from 'react';

import { type QueryClient, useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from 'api/constants';
import type { MetaApiClient } from 'api/meta/client-metaapi';
import { useMetaApiClient } from 'api/meta/client-metaapi';
import type { ProjectTeamMappingsActions } from 'state/projectTeamMappingState/projectTeamMappingsActions';
import { useProjectTeamMappingsActions } from 'state/projectTeamMappingState/projectTeamMappingsActions';

import { RELEASE_TEMPLATE_WIZARD_STORE } from './releaseTemplateWizardState';

export const useReleaseTemplateWizardActions = () => {
  const metaApiClient = useMetaApiClient();
  const projectTeamMappingActions = useProjectTeamMappingsActions();
  const queryClient = useQueryClient();
  return useMemo(() => {
    return new ReleaseTemplateWizardActions(metaApiClient, projectTeamMappingActions, queryClient);
  }, [projectTeamMappingActions, metaApiClient, queryClient]);
};

export class ReleaseTemplateWizardActions {
  private metaApiClient!: MetaApiClient;
  private projectTeamMappingActions!: ProjectTeamMappingsActions;
  private queryClient!: QueryClient;

  constructor(
    metaApiClient: MetaApiClient,
    projectTeamMappingActions: ProjectTeamMappingsActions,
    queryClient: QueryClient
  ) {
    this.metaApiClient = metaApiClient;
    this.projectTeamMappingActions = projectTeamMappingActions;
    this.queryClient = queryClient;
  }

  public async requestTemplateRelease(templateId: string) {
    RELEASE_TEMPLATE_WIZARD_STORE.getState().setReleaseRequested(templateId);
    RELEASE_TEMPLATE_WIZARD_STORE.getState().setIsReleaseInProgress(true);
    await this.metaApiClient.SetTemplateReleased(templateId);
  }

  public async unreleaseTemplate(templateId: string) {
    await this.metaApiClient.SetTemplateUnReleased(templateId);

    // TODO templates-performance: Update cache by setting releasedOn to null instead of invalidating
    // Note: useSetTemplateUnReleased already does this - this is a duplicate invalidation
    this.queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEMPLATES] });
    this.queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEMPLATES, templateId] });
    RELEASE_TEMPLATE_WIZARD_STORE.setState({ releaseProcessState: 'idle' });
  }

  async handleTemplateReleased(templateId: string, success: boolean, messages: string[]) {
    RELEASE_TEMPLATE_WIZARD_STORE.getState().setValidationResult(templateId, success, messages);
    const { IMPORT_MAPPINGS } = QUERY_KEYS;

    if (!success) {
      RELEASE_TEMPLATE_WIZARD_STORE.getState().setIsReleaseInProgress(false);
      return;
    }

    if (success) {
      RELEASE_TEMPLATE_WIZARD_STORE.getState().setUpdatingDependencies();

      RELEASE_TEMPLATE_WIZARD_STORE.getState().setNumberOfMigratedItems(0);
      const projectTeamConfigurationMappingsToMigrate =
        RELEASE_TEMPLATE_WIZARD_STORE.getState().projectTeamConfigurationMappings.filter((x) => x.migrate); //;((state) => state.projectTeamConfigurationMappings);
      const importMappingsToMigrate = RELEASE_TEMPLATE_WIZARD_STORE.getState().importMappingsToMigrate.filter(
        (x) => x.migrate
      ); //;((state) => state.projectTeamConfigurationMappings);

      RELEASE_TEMPLATE_WIZARD_STORE.getState().setNumberOfItemsToMigrate(
        projectTeamConfigurationMappingsToMigrate.length + importMappingsToMigrate.length
      );

      for await (const item of projectTeamConfigurationMappingsToMigrate) {
        const migrationResult = await this.projectTeamMappingActions.MigrateProjectTeamMapping(
          item.mappingId,
          templateId
        );
        if (!migrationResult.success) {
          alert(`Migration of team ${item.projectTeamName} failed`);
        } else {
          RELEASE_TEMPLATE_WIZARD_STORE.getState().increaseNumberOfMigratedItems();
        }
      }
      this.queryClient.invalidateQueries({ queryKey: [IMPORT_MAPPINGS, templateId] });

      RELEASE_TEMPLATE_WIZARD_STORE.getState().setIsReleaseInProgress(false);
      RELEASE_TEMPLATE_WIZARD_STORE.getState().setReleaseComplete();
    }
  }
}
